import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography,
  InputAdornment,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar
} from '@mui/material';
import { motion } from 'framer-motion';
import { 
  ChevronRight, 
  ChevronLeft, 
  User,
  CheckCircle 
} from 'lucide-react';
import { 
  RegisterCard, 
  ActionButton, 
  BackButton, 
  StyledTextField, 
  AuthCard 
} from '../styles/StyledComponents';
import { validateRUT, validateSSN } from '../utils/validators';
import { HEALTH_GREEN } from '../utils/mockData';
import AppleSpinner from '../../../common/AppleSpinner';
import CountrySelector from '../components/CountrySelector';
import StepperWrapper from '../components/StepperWrapper';
import PasswordFields from '../components/PasswordFields';
import UserTypeSelector from '../components/UserTypeSelector';
import agthealthService from '../../../services/agthealthService';

const specialties = [
 // Especialidades Médicas
 'Cardiología',
 'Dermatología',
 'Endocrinología',
 'Medicina Familiar',
 'Gastroenterología',
 'Medicina General',
 'Geriatría',
 'Medicina Interna',
 'Neurología',
 'Obstetricia',
 'Ginecología',
 'Oncología',
 'Oftalmología',
 'Ortopedia',
 'Pediatría',
 'Psiquiatría',
 'Neumología',
 'Radiología',
 'Cirugía',
 'Urología',

 // Otras Profesiones de la Salud
 'Enfermería',
 'Farmacia',
 'Fisioterapia',
 'Terapia Ocupacional',
 'Fonoaudiología',
 'Psicología',
 'Nutrición/Dietética',
 'Trabajo Social',
 'Terapia Respiratoria',
 'Tecnología Médica',
 'Medicina de Laboratorio',
 'Anestesiología',
 'Medicina de Urgencia',
 'Patología',
 'Tecnología en Radiología',
 'Odontología',
 'Optometría',
 'Podología',
 'Quiropráctica',
 'Obstetricia',
 'Asistente Médico',
 'Paramédico/TUM',
 'Otro'
];

const RegisterViewIndependent = ({ onBackToLogin, isMobile, userType, onUserTypeChange }) => {
  // Registration states
  const [activeStep, setActiveStep] = useState(0);
  const [country, setCountry] = useState('');
  const [identificationNumber, setIdentificationNumber] = useState('');
  const [identificationError, setIdentificationError] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(''); // Nuevo estado para phoneNumber
  const [specialty, setSpecialty] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'error' });

  // Get registration steps
  const getRegistrationSteps = () => {
    return isMobile
      ? ['Country', 'ID', 'Info', 'Specialty', 'Password']
      : ['Country', 'Identification', 'Personal Info', 'Professional Details', 'Create Password'];
  };

  const registrationSteps = getRegistrationSteps();

  // Validate email format
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // Handle password changes
  const handleNewPasswordChange = (e) => {
    setPassword(e.target.value);
    validatePasswords(e.target.value, confirmPassword);
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    validatePasswords(password, e.target.value);
  };

  const validatePasswords = (newPwd, confirmPwd) => {
    if (confirmPwd && newPwd !== confirmPwd) {
      setPasswordError('Passwords do not match');
      return false;
    } else if (newPwd && newPwd.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return false;
    } else {
      setPasswordError('');
      return true;
    }
  };

  // Registration navigation
  const handleNext = () => {
    // Validate before proceeding
    if (activeStep === 1) {
      if (country === 'cl' && !validateRUT(identificationNumber)) {
        setIdentificationError('Invalid RUT format. Example: 12345678-9');
        return;
      } else if (country === 'us' && !validateSSN(identificationNumber)) {
        setIdentificationError('Invalid SSN format. Example: 123-45-6789');
        return;
      }
      setIdentificationError('');
    }
    
    // For step 2 (Personal Info), validate email and phone
    if (activeStep === 2) {
      if (!firstName || !lastName || !email || !phoneNumber) {
        setAlert({
          open: true,
          message: 'All fields are required',
          severity: 'error'
        });
        return;
      }
      
      if (!validateEmail(email)) {
        setEmailError('Please enter a valid email address');
        return;
      }
      
      setEmailError('');
    }
    
    // For step 3 (Professional Details), validate specialty
    if (activeStep === 3) {
      if (!specialty) {
        setAlert({
          open: true,
          message: 'Please select a specialty',
          severity: 'error'
        });
        return;
      }
    }
    
    // For final step (Create Password), validate passwords
    if (activeStep === 4) {
      if (!validatePasswords(password, confirmPassword)) {
        return;
      }
      handleRegister();
      return;
    }
    
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    if (activeStep === 0) {
      onBackToLogin();
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleRegister = async () => {
    setIsSubmitting(true);
    
    try {
      const userData = {
        email,
        password,
        countryCode: country,
        identificationNumber,
        firstName,
        lastName,
        specialtyArea: specialty,
        professionalLicense: licenseNumber,
        phoneNumber // Añadir el número de teléfono
      };
      
      const response = await agthealthService.registerIndependent(userData);
      
      if (response.success) {
        setRegisterSuccess(true);
      } else {
        throw new Error(response.message || 'Error registering user');
      }
    } catch (error) {
      console.error('Error registering independent professional:', error);
      setAlert({
        open: true,
        message: error.response?.data?.message || 'Error registering account',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Determine if the current step is complete
  const isStepComplete = () => {
    switch (activeStep) {
      case 0: return !!country;
      case 1: return !!identificationNumber && !identificationError;
      case 2: return !!firstName && !!lastName && !!email && !!phoneNumber && !emailError;
      case 3: return !!specialty;
      case 4: return password && confirmPassword && !passwordError && password.length >= 8;
      default: return false;
    }
  };

  // Registration step content
  const getStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <CountrySelector 
            selectedCountry={country} 
            onSelectCountry={setCountry} 
          />
        );
      
      case 1:
        return (
          <Box sx={{ mb: 4 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {country === 'cl' 
                ? 'Please enter your RUT (without dots and with dash)' 
                : 'Please enter your Social Security Number'}
            </Typography>
            <StyledTextField
              fullWidth
              label={country === 'cl' ? 'RUT' : 'SSN'}
              variant="outlined"
              value={identificationNumber}
              onChange={(e) => {
                setIdentificationNumber(e.target.value);
                setIdentificationError(''); // Clear error on change
              }}
              placeholder={country === 'cl' ? '12345678-9' : '123-45-6789'}
              error={!!identificationError}
              helperText={identificationError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box sx={{ width: 24, height: 16, marginRight: 0 }}>
                      <Box 
                        component="img" 
                        src={`https://flagcdn.com/w40/${country}.png`}
                        alt={`${country === 'cl' ? 'Chile' : 'US'} flag`}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </Box>
                  </InputAdornment>
                ),
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {country === 'cl' 
                ? 'Example: 12345678-9' 
                : 'Example: 123-45-6789'}
            </Typography>
          </Box>
        );
      
      case 2:
        return (
          <Box sx={{ mb: 4 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Enter your personal information
            </Typography>
            
            <StyledTextField
              fullWidth
              label="First Name"
              variant="outlined"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              margin="normal"
            />
            
            <StyledTextField
              fullWidth
              label="Last Name"
              variant="outlined"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              margin="normal"
            />
            
            <StyledTextField
              fullWidth
              label="Email"
              variant="outlined"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError('');
              }}
              error={!!emailError}
              helperText={emailError}
              margin="normal"
            />
            
            <StyledTextField
              fullWidth
              label="Phone Number"
              variant="outlined"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              margin="normal"
              required
            />
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Your phone number is required for sending notifications. Your contact information will not be shared with patients.
            </Typography>
          </Box>
        );
      
      case 3:
        return (
          <Box sx={{ mb: 4 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Enter your professional details
            </Typography>
            
            <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
              <InputLabel id="specialty-label">Medical Specialty</InputLabel>
              <Select
                labelId="specialty-label"
                value={specialty}
                label="Medical Specialty"
                onChange={(e) => setSpecialty(e.target.value)}
              >
                {specialties.map((spec) => (
                  <MenuItem key={spec} value={spec}>{spec}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <StyledTextField
              fullWidth
              label="Professional License Number (optional)"
              variant="outlined"
              value={licenseNumber}
              onChange={(e) => setLicenseNumber(e.target.value)}
              margin="normal"
            />
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Your professional information will be used to customize your experience
            </Typography>
          </Box>
        );
      
      case 4:
        return (
          <PasswordFields
            newPassword={password}
            confirmPassword={confirmPassword}
            passwordError={passwordError}
            onNewPasswordChange={handleNewPasswordChange}
            onConfirmPasswordChange={handleConfirmPasswordChange}
            isMobile={isMobile}
          />
        );
      
      default:
        return 'Unknown step';
    }
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  if (registerSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ width: '100%' }}
      >
        <AuthCard>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            textAlign: 'center',
            my: 4
          }}>
            <CheckCircle size={isMobile ? 64 : 80} color={HEALTH_GREEN} />
            <Typography variant={isMobile ? "h5" : "h4"} sx={{ mt: 3, fontWeight: 600 }}>
              Registration Complete
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              Your account has been created successfully.
              You can now access all features of AGT Health.
            </Typography>
            
            <ActionButton 
              variant="contained"
              disableElevation
              fullWidth
              onClick={onBackToLogin}
              sx={{ mt: 4 }}
            >
              Return to Login
            </ActionButton>
          </Box>
        </AuthCard>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ width: '100%' }}
    >
      <Snackbar 
        open={alert.open} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
      
      <RegisterCard>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          mb: isMobile ? 2 : 3, 
          alignItems: 'center'
        }}>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            sx={{ 
              fontWeight: 600, 
              mb: 1, 
              display: 'flex', 
              alignItems: 'center' 
            }}
          >
            AGT Health
            <motion.span
              initial={{ scale: 1 }}
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 1, repeat: Infinity, repeatDelay: 5 }}
              style={{ 
                color: HEALTH_GREEN, 
                fontSize: isMobile ? '24px' : '32px', 
                fontWeight: 700, 
                marginLeft: '2px',
                position: 'relative',
                top: '-8px'
              }}
            >
              +
            </motion.span>
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <User size={isMobile ? 18 : 20} color={HEALTH_GREEN} />
            <Typography variant="subtitle1" color="text.secondary">
              Create Independent Professional Account
            </Typography>
          </Box>
        </Box>

        <UserTypeSelector 
          userType={userType} 
          onUserTypeChange={onUserTypeChange} 
        />

        <StepperWrapper 
          steps={registrationSteps}
          activeStep={activeStep}
        />

        {getStepContent()}

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <BackButton
            startIcon={<ChevronLeft size={16} />}
            onClick={handleBack}
            disabled={isSubmitting}
            fullWidth={isMobile}
          >
            {activeStep === 0 ? 'Back to Login' : 'Back'}
          </BackButton>
          
          <ActionButton 
            variant="contained"
            disableElevation
            disabled={!isStepComplete() || isSubmitting}
            onClick={handleNext}
            fullWidth={isMobile}
            endIcon={activeStep === registrationSteps.length - 1 ? null : <ChevronRight size={16} />}
          >
            {isSubmitting ? <AppleSpinner sizeDesktop={24} sizeMobile={20} /> : 
              (activeStep === registrationSteps.length - 1 ? 'Complete Registration' : 'Continue')}
          </ActionButton>
        </Box>
      </RegisterCard>
    </motion.div>
  );
};

export default RegisterViewIndependent;